import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Company } from "@type/company";
import { useQuery } from "@util/useQuery";
import { CheckCircleIcon, EyeIcon, EyeOffIcon, Loader2Icon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BasicLayout from "@container/layout/basic";
import { getCompanyInfo, updateCompanyInfo, updatePreferenceCompanyLogo } from "@service/user";
import { createPassword } from "@service/auth";
import { Textarea } from "@component/textarea";
import { catchErrorLog } from "@util/sentry";
import { removeCookie, setCookie } from "@util/cookie";

export default function WorkspaceSetupPage() {
    const queryParams = useQuery()
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [companyInfo, setCompanyInfo] = useState<Company | null>(null);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});
    const [currentStep, setCurrentStep] = useState<"setup-password" | "setup-company" | "complete">("setup-password");
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [companyName, setCompanyName] = useState("");
    const [companyDescription, setCompanyDescription] = useState("");
    const [companyLinkedIn, setCompanyLinkedIn] = useState("");
    const [companyWebsite, setCompanyWebsite] = useState("");

    const fileRef = useRef<HTMLInputElement>(null);

    const token = queryParams.get("token") || "";
    const payloadToken = token ? JSON.parse(atob(token)) : null;

    useEffect(() => {
        if (payloadToken && payloadToken.company_id) {
            getCompanyInfo(payloadToken.company_id).then((resp) => {
                if (resp.status === 200) {
                    setCookie('tempAuthToken', payloadToken.access_token);
                    setCompanyInfo(resp.data.data);
                    setCompanyName(resp.data.data.company_name || "");
                    setCompanyDescription(resp.data.data.company_description || "");
                    setCompanyLinkedIn(resp.data.data.company_linkedin || "");
                    setCompanyWebsite(resp.data.data.company_website || "");
                    setLogoPreview(resp.data.data.preferences?.company_logo || null);
                    if (!resp.data.data.workspace_access_date) {
                        return navigate("/verify/activated-workspace?token=" + queryParams.get("token"));
                    }
                }
            }).finally(() => {
                setIsLoading(false);
            })
        }
    }, [])

    // Some workspace types (agent_calling / training-agent) have their
    // company details pre-provisioned and don't need the setup-company
    // step. For those we jump straight from password to complete and
    // perform the same auth-cookie swap that the company-info submit
    // would normally do.
    const shouldSkipCompanySetup = (
        companyInfo?.type === "agent_calling" || companyInfo?.type === "training-agent"
    );

    const finalizeSetup = () => {
        setCurrentStep("complete");
        removeCookie("tempAuthToken");
        setCookie('authToken', payloadToken.access_token);
    };

    const advanceAfterPasswordStep = () => {
        if (shouldSkipCompanySetup) {
            finalizeSetup();
        } else {
            setCurrentStep("setup-company");
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!companyInfo) return;

        if (password.length < 8) {
            setErrors({ password: "Password must be at least 8 characters long." });
            return;
        } else {
            setErrors((prev) => ({ ...prev, password: undefined }));
        }

        if (password !== confirmPassword) {
            setErrors({ confirmPassword: "Passwords do not match." });
            return;
        } else {
            setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
        }

        setIsLoading(true);
        createPassword({
            new_password: password,
            confirm_new_password: confirmPassword,
        }).then((resp) => {
            if (resp.status === 200) {
                advanceAfterPasswordStep();
            }
        }).catch((error) => {
            if (error?.response?.data?.message === "Password already exists") {
                advanceAfterPasswordStep();
            }
        }).finally(() => {
            setIsLoading(false);
        })
    }

    const handleLogoUpload = async (file: File) => {
        try {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            const resp = await updatePreferenceCompanyLogo(file);
            if (resp.status === 200) {
                setLogoPreview(resp.data.data.company_logo_path);
            }
        } catch (error) {
            catchErrorLog(`Error uploading logo: ${error}`);
        }
    };

    const handleSubmitCompanyInfo = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setIsLoading(true);
        updateCompanyInfo({
            company_name: companyName,
            company_description: companyDescription,
            company_linkedin: companyLinkedIn,
            company_website: companyWebsite,
        }).then((resp) => {
            if (resp.status === 200) {
                finalizeSetup();
            }
        }).finally(() => {
            setIsLoading(false);
        })
    }

    return (
        <BasicLayout showFooter={false}>

            {currentStep === "setup-password" && (
                <div className="flex items-center justify-center min-h-full px-4">
                    <Card className="w-full max-w-md shadow-md">
                        <CardContent className="p-8 space-y-6">
                            {/* Header */}
                            <div className="text-center space-y-2">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    Create a New Password
                                </h2>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    This password will be used to sign in to your <span className="font-bold text-gray-900 dark:text-white">{companyInfo?.company_name}</span> workspace.
                                </p>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="space-y-5">
                                {/* Email Field (Read-only) */}
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Email Address
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={payloadToken.workspace_email}
                                        readOnly
                                        disabled
                                        className="bg-gray-50 dark:bg-gray-800 cursor-not-allowed"
                                    />
                                </div>

                                {/* Password Field */}
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Password
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Enter your password"
                                            className={errors.password ? "border-red-500" : ""}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                        >
                                            {showPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <p className="text-xs text-red-500">{errors.password}</p>
                                    )}
                                </div>

                                {/* Confirm Password Field */}
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Confirm Password
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="confirmPassword"
                                            type={showConfirmPassword ? "text" : "password"}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Confirm your password"
                                            className={errors.confirmPassword ? "border-red-500" : ""}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                        >
                                            {showConfirmPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    {errors.confirmPassword && (
                                        <p className="text-xs text-red-500">{errors.confirmPassword}</p>
                                    )}
                                </div>
                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    disabled={isLoading || !password || !confirmPassword}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                    {(isLoading && password && confirmPassword) ? (
                                        <>
                                            <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                                            Setting up password...
                                        </>
                                    ) : (
                                        "Continue"
                                    )}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            )}

            {currentStep === "setup-company" && (
                <Card className="w-full max-w-2xl shadow-md">
                    <CardContent className="p-8">
                        <form onSubmit={handleSubmitCompanyInfo} className="space-y-6">
                            {/* Header */}
                            <div className="text-center space-y-2">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    Review Your Company Details
                                </h2>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    This information makes up your company details.
                                    <br />
                                    You can revise it at any time in your dashboard.
                                </p>
                            </div>

                            {/* Form Fields - Vertical Stack */}
                            <div className="space-y-5">
                                {/* Company Name */}
                                <div className="space-y-2">
                                    <Label htmlFor="companyName" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        Company Name <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="companyName"
                                        type="text"
                                        placeholder="Enter company name"
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        required
                                        className="w-full"
                                    />
                                </div>

                                {/* Company Description */}
                                <div className="space-y-2">
                                    <Label htmlFor="companyDescription" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        Company Description
                                    </Label>
                                    <Textarea
                                        id="companyDescription"
                                        placeholder="Describe your company"
                                        value={companyDescription}
                                        onChange={(e) => setCompanyDescription(e.target.value)}
                                        rows={5}
                                        className="w-full resize-none"
                                    />
                                </div>

                                {/* Company Logo */}
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        Company Logo
                                    </Label>
                                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8">
                                        <div className="flex flex-col items-center justify-center space-y-4">
                                            {/* Logo Preview */}
                                            {logoPreview && (
                                                <div className="relative flex-shrink-0 w-20 h-20 rounded-full overflow-hidden flex justify-center items-center bg-white ring-2 ring-blue-200 dark:ring-offset-gray-900">
                                                    <img
                                                        src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${logoPreview || "commons/ourteam_logo_only.png"}`}
                                                        alt={`${companyName} logo`}
                                                        onError={(e) => {
                                                            e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/company_placeholder.png`;
                                                        }}
                                                        className="relative w-20 object-cover "
                                                    />
                                                </div>
                                            )}

                                            <input
                                                ref={fileRef}
                                                type="file"
                                                id="logoUpload"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    if (e.target.files && e.target.files[0]) {
                                                        handleLogoUpload(e.target.files[0]);
                                                    }
                                                }}
                                                className="hidden"
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => fileRef.current?.click()}
                                                className="font-medium"
                                            >
                                                Upload Logo
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                {/* Company LinkedIn & Website */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="companyLinkedIn" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                            Company LinkedIn
                                        </Label>
                                        <Input
                                            id="companyLinkedIn"
                                            type="url"
                                            placeholder="https://linkedin.com/company/your-company"
                                            value={companyLinkedIn}
                                            onChange={(e) => setCompanyLinkedIn(e.target.value)}
                                            className="w-full"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="companyWebsite" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                            Company Website
                                        </Label>
                                        <Input
                                            id="companyWebsite"
                                            type="url"
                                            placeholder="https://www.yourcompany.com"
                                            value={companyWebsite}
                                            onChange={(e) => setCompanyWebsite(e.target.value)}
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="pt-4">
                                <Button
                                    type="submit"
                                    disabled={isLoading || !companyName}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 font-semibold"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        "Submit"
                                    )}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}

            {currentStep === "complete" && (
                <Card className="w-full max-w-md mx-auto border border-border shadow-sm">
                    <CardContent className="p-8 space-y-6">
                        <div className="flex justify-center">
                            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                                <CheckCircleIcon className="w-10 h-10 text-green-600 dark:text-green-500" />
                            </div>
                        </div>

                        <div className="text-center space-y-2">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                Workspace Activated!
                            </h2>
                            <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                                <p>Your workspace has been successfully activated.</p>
                                <p>You are now the workspace owner.</p>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <Button
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                                style={{ paddingTop: "16px", paddingBottom: "16px" }}
                                onClick={() => {
                                    window.location.href = "/dashboard"
                                }}
                            >
                                Login to Workspace
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}
        </BasicLayout>
    )
}